import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Edit2, Plus, Trash2, CheckSquare, Square } from "lucide-react";
import AppModal from "../../components/AppModal";
import DataTable from "../../components/DataTable";
import ResourcePage from "../../components/ResourcePage";
import SqlImportButton from "../../components/SqlImportButton";
import { api } from "../../axios/api";
import { confirmAction, notify } from "../../utils/toastAlert";

const TIPOS_SERIES = [
  { value: "FA", label: "FA - Fatura" },
  { value: "FT", label: "FT - Fatura/Recibo" },
  { value: "FR", label: "FR - Fatura Simplificada" },
  { value: "FG", label: "FG - Fatura Global" },
  { value: "GF", label: "GF - Guia de Remessa" },
  { value: "AC", label: "AC - Ajuste de Conta" },
  { value: "AR", label: "AR - Anulação de Recibo" },
  { value: "TV", label: "TV - Talão de Venda" },
  { value: "RC", label: "RC - Recibo" },
  { value: "RG", label: "RG - Recibo Global" },
  { value: "RE", label: "RE - Reembolso" },
  { value: "ND", label: "ND - Nota de Débito" },
  { value: "NC", label: "NC - Nota de Crédito" },
  { value: "NE", label: "NE - Nota de Encomenda" },
  { value: "AF", label: "AF - Adiantamento de Fatura" },
  { value: "RP", label: "RP - Recibo Provisório" },
  { value: "RA", label: "RA - Recibo de Adiantamento" },
  { value: "CS", label: "CS - Conta Corrente" },
  { value: "LD", label: "LD - Livro de Diário" },
  { value: "FP", label: "FP - Fatura Pro-forma" },
];

const emptyForm = {
  ano: new Date().getFullYear(),
  tipos: [],
};

const closeModal = (id) => {
  const element = document.getElementById(id);
  if (!element || !window.bootstrap) return;
  window.bootstrap.Modal.getOrCreateInstance(element).hide();
};

const showModal = (id) => {
  const element = document.getElementById(id);
  if (!element || !window.bootstrap) return;
  window.bootstrap.Modal.getOrCreateInstance(element).show();
};

const Series = () => {
  const user = useSelector((state) => state.user.user);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);

  const fetchSeries = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/series");
      const allSeries = response.data?.data || response.data || [];
      const companySeries = allSeries.filter(
        (item) => Number(item.empresa_id) === Number(user?.empresa_id)
      );
      setSeries(companySeries);
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao carregar séries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.empresa_id) {
      fetchSeries();
    }
  }, [user?.empresa_id]);

  const handleChange = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const handleCheckboxChange = (tipo) => {
    setForm((current) => {
      const exists = current.tipos.includes(tipo);
      if (exists) {
        return { ...current, tipos: current.tipos.filter((t) => t !== tipo) };
      } else {
        return { ...current, tipos: [...current.tipos, tipo] };
      }
    });
  };

  const handleSelectAll = () => {
    if (form.tipos.length === TIPOS_SERIES.length) {
      setForm((current) => ({ ...current, tipos: [] }));
    } else {
      setForm((current) => ({ ...current, tipos: TIPOS_SERIES.map((t) => t.value) }));
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ano: new Date().getFullYear(), tipos: [] });
    showModal("serieFormModal");
  };

  const openEdit = (item = selected) => {
    if (!item) return;
    setEditing(item);
    setForm({
      nome: item.nome || "",
      ano: item.ano || new Date().getFullYear(),
      status: item.status ? "1" : "0",
    });
    showModal("serieFormModal");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      if (editing) {
        const payload = {
          nome: form.nome,
          ano: form.ano,
          status: form.status === "1" || form.status === true,
        };
        await api.post(`/series/${editing.id}`, payload);
        await notify("Série actualizada com sucesso.", "success");
      } else {
        const payload = {
          empresa_id: user.empresa_id,
          ano: form.ano,
          tipos: form.tipos,
        };
        await api.post("/series/solicitar-multiplas", payload);
        await notify("Séries solicitadas com sucesso.", "success");
      }

      setForm(emptyForm);
      setEditing(null);
      closeModal("serieFormModal");
      fetchSeries();
    } catch (err) {
      await notify(err.response?.data?.message || "Não foi possível guardar a série.", "error");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!selected) return;
    const question = await confirmAction({
      title: "Excluir série",
      text: `Deseja excluir a série ${selected.nome}?`,
      icon: "warning",
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
    });

    if (!question.isConfirmed) return;

    try {
      setLoading(true);
      await api.delete(`/series/${selected.id}`);
      await notify("Série excluída com sucesso.", "success");
      setSelected(null);
      fetchSeries();
    } catch (err) {
      await notify(err.response?.data?.message || "Não foi possível excluir a série.", "error");
    } finally {
      setLoading(false);
    }
  };

  const canManage = user?.nivel === "admin";

  return (
    <ResourcePage
      title="Séries"
      breadcrumb={["Gestão", "Séries"]}
      actions={(
        <>
          <SqlImportButton endpoint="/importar/series" label="Importar series" disabled={loading || !canManage} onImported={fetchSeries} />
          <button className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1" type="button" onClick={openCreate} disabled={!canManage}>
            <Plus size={16} />
            Solicitar Séries
          </button>
          <button className="btn btn-light btn-sm d-inline-flex align-items-center gap-1" type="button" onClick={() => openEdit()} disabled={!canManage || !selected}>
            <Edit2 size={16} />
            Editar
          </button>
          <button className="btn btn-light text-danger btn-sm d-inline-flex align-items-center gap-1" type="button" onClick={confirmDelete} disabled={!canManage || !selected}>
            <Trash2 size={16} />
            Excluir
          </button>
        </>
      )}
    >
      <div className="card app-card bg-white">
        <div className="card-body">
          {error && <div className="alert alert-danger py-2">{error}</div>}
          {!canManage && <div className="alert alert-warning py-2">Apenas administradores podem gerir séries.</div>}

          <DataTable
            id="seriesDataTable"
            columns={["Tipo", "Nome", "Quantidade", "1º Documento", "Último Documento", "Ano", "Estado"]}
            rows={series}
            renderRow={(item) => (
              <tr key={item.id} className={selected?.id === item.id ? "table-active" : ""} onClick={() => setSelected(item)}>
                <td><span className="badge bg-primary-subtle text-primary">{item.tipo}</span></td>
                <td>{item.nome}</td>
                <td>{item.quantidade}</td>
                <td>{item.primeiroDocumento || "-"}</td>
                <td>{item.ultimoDocumento || "-"}</td>
                <td>{item.ano}</td>
                <td><span className={`badge ${item.status ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"}`}>{item.status ? "Activo" : "Inactivo"}</span></td>
              </tr>
            )}
          />
        </div>
      </div>

      <AppModal
        id="serieFormModal"
        title={editing ? "Editar série" : "Solicitar novas séries"}
        footer={(
          <>
            <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
            <button type="submit" className="btn btn-primary" form="serieForm" disabled={loading || !canManage}>
              {loading ? "A processar..." : "Guardar"}
            </button>
          </>
        )}
      >
        <form id="serieForm" onSubmit={handleSubmit}>
          {editing ? (
            <div className="row">
              <div className="col-md-8 mb-3">
                <label className="form-label" htmlFor="nome">Nome</label>
                <input className="form-control" id="nome" name="nome" value={form.nome} onChange={handleChange} required />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label" htmlFor="ano">Ano</label>
                <input className="form-control" id="ano" name="ano" type="number" value={form.ano} onChange={handleChange} required />
              </div>
              <div className="col-md-12 mb-3">
                <label className="form-label" htmlFor="status">Estado</label>
                <select className="form-select" id="status" name="status" value={form.status} onChange={handleChange}>
                  <option value="1">Activo</option>
                  <option value="0">Inactivo</option>
                </select>
              </div>
            </div>
          ) : (
            <div className="row">
              <div className="col-md-12 mb-3">
                <label className="form-label" htmlFor="ano">Ano de Referência</label>
                <input className="form-control" id="ano" name="ano" type="number" value={form.ano} onChange={handleChange} required />
              </div>
              <div className="col-md-12 mb-2 d-flex justify-content-between align-items-center">
                <label className="form-label fw-bold mb-0">Selecione os tipos de séries:</label>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleSelectAll}>
                  {form.tipos.length === TIPOS_SERIES.length ? "Desmarcar Todos" : "Selecionar Todos"}
                </button>
              </div>
              <div className="col-md-12">
                <div className="row g-2 border rounded p-3 bg-light" style={{ maxHeight: "250px", overflowY: "auto" }}>
                  {TIPOS_SERIES.map((item) => {
                    const checked = form.tipos.includes(item.value);
                    return (
                      <div className="col-md-6" key={item.value}>
                        <div
                          className={`form-check p-2 rounded border bg-white cursor-pointer d-flex align-items-center gap-2 ${checked ? "border-primary bg-primary-subtle" : ""}`}
                          onClick={() => handleCheckboxChange(item.value)}
                          style={{ cursor: "pointer" }}
                        >
                          <input
                            className="form-check-input m-0"
                            type="checkbox"
                            checked={checked}
                            onChange={() => {}}
                            id={`tipo_${item.value}`}
                          />
                          <label className="form-check-label m-0 cursor-pointer w-100" style={{ cursor: "pointer" }}>
                            {item.label}
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </form>
      </AppModal>
    </ResourcePage>
  );
};

export default Series;
